<template>
  <div
    class="release-highlights-list"
    data-qa="release-highlights-list"
  >
    <div
      class="release-highlights-list__layout"
      :class="{
        'release-highlights-list__layout_with-table-of-contents': hasTableOfContents && selectedReleases.length > 0,
      }"
    >
      <div class="release-highlights-list__releases">
        <p
          v-if="selectedReleases.length === 0"
          class="release-highlights-list__empty"
        >
          {{ t('empty') }}
        </p>
        <section
          v-for="release in selectedReleases"
          :id="hasTableOfContents ? createReleaseAnchorId(release.version) : undefined"
          :key="release.version"
          class="release-highlights-list__release flex-column"
          data-qa="release-highlights-list__release"
          :data-qa-id="release.version"
        >
          <header class="release-highlights-list__release-header flex-row flex-wrap">
            <h2 class="release-highlights-list__version typo-h4">
              {{ t('version', {
                version: release.version,
              }) }}
            </h2>
            <time
              v-if="release.releaseDate"
              class="release-highlights-list__release-date"
              :datetime="release.releaseDate"
            >
              {{ formatReleaseDate(release.releaseDate) }}
            </time>
          </header>
          <div class="flex-column-section">
            <ReleaseHighlight
              v-for="(highlight, index) in release.highlights"
              :key="index"
              :highlight="highlight"
              :media-folder-url="`${mediaBaseUrl}/${release.version}`"
            />
            <section
              v-if="release.releaseNotes !== undefined"
              class="flex-column"
              data-qa="release-highlights-list__release-notes"
            >
              <h3
                v-if="release.highlights.length > 0"
                class="release-highlights-list__release-notes-headline typo-h5 text-bold"
              >
                {{ t('releaseNotes', {
                  version: release.version,
                }) }}
              </h3>
              <ReleaseMarkdown :markdown="release.releaseNotes" />
            </section>
          </div>
        </section>
      </div>
      <aside
        v-if="hasTableOfContents && selectedReleases.length > 0"
        class="release-highlights-list__aside flex-column-section"
      >
        <slot name="table-of-contents-header" />
        <QuickNavigation
          class="release-highlights-list__quick-navigation"
          :title="t('versions')"
          :items="quickNavigationItems"
          updates-url-hash
        />
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
} from 'vue';

import useLibraryI18n from '../../i18n/composable.ts';
import dayjs from '../../plugins/dayjs.ts';
import type {
  TIsoDate,
} from '../../utils/date.ts';
import type {
  IQuickNavigationItem,
} from '../quick-navigation/quick-navigation.ts';
import QuickNavigation from '../quick-navigation/quick-navigation.vue';

import ReleaseHighlight from './release-highlight.vue';
import {
  type TRelease,
  createReleaseAnchorId,
  createReleaseQuickNavigationItems,
  selectReleases,
} from './release-highlights.ts';
import ReleaseMarkdown from './release-markdown.vue';

const props = defineProps<{
  /** In any order; shown newest first. Releases without highlights show only their release notes. */
  releases: TRelease[],
  /** Releases after this version are not shown, e.g. those prepared for the next release. */
  currentVersion: string,
  /** Only releases after this version are shown. Without it, all releases up to `currentVersion` are shown. */
  lastKnownVersion?: string,
  /**
   * URL path under which the media of each release is served in a folder named after its
   * version, e.g. `/release-highlights` for `/release-highlights/2.14.0/chart.png`.
   */
  mediaBaseUrl: string,
  /**
   * Shows the versions next to the releases, with the version in view marked, to jump to a
   * version and to link to it. Hidden when the list is too narrow.
   */
  hasTableOfContents?: boolean,
}>();

defineSlots<{
  /** Shown above the versions, e.g. a link to the full changelog. */
  'table-of-contents-header'?(): any,
}>();

const {
  t,
  locale,
} = useLibraryI18n('components/release-highlights/release-highlights-list');

const selectedReleases = computed<TRelease[]>(() => selectReleases({
  releases: props.releases,
  currentVersion: props.currentVersion,
  lastKnownVersion: props.lastKnownVersion,
  allowReleasesWithoutHighlights: true,
}));

function formatReleaseDate(releaseDate: TIsoDate): string {
  // Read so that the dates are formatted again when the locale changes.
  void locale.value;
  return dayjs(releaseDate).format('LL');
}

const quickNavigationItems = computed<IQuickNavigationItem[]>(() => createReleaseQuickNavigationItems({
  releasesNewestFirst: selectedReleases.value,
  locale: locale.value,
}));
</script>

<style scoped lang="scss">
.release-highlights-list {
  container-type: inline-size;

  &__layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--gap-section);

    &_with-table-of-contents {
      grid-template-columns: minmax(0, 1fr) 14.5rem;
    }
  }

  &__releases {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 48rem;
    margin-inline: auto;
  }

  &__release {
    & + & {
      margin-top: var(--gap-section);
      padding-top: var(--gap-section);
      border-top: 1px solid var(--border-color-strong);
    }
  }

  &__release-header {
    align-items: baseline;
  }

  &__release-date,
  &__empty {
    color: var(--text-secondary-color);
  }

  &__aside {
    position: sticky;
    top: 0;
    align-self: start;
  }

  &__quick-navigation {
    max-height: calc(100dvh - 14rem);
    overflow-y: auto;
  }
}

// The versions need room next to the releases. On narrow screens they are hidden, and what is
// shown above them moves above the releases.
@container (width < 40rem) {
  .release-highlights-list__layout_with-table-of-contents {
    grid-template-columns: minmax(0, 1fr);
  }

  .release-highlights-list__aside {
    position: static;
    order: -1;
  }

  .release-highlights-list__quick-navigation {
    display: none;
  }
}
</style>
